import Device, { SENSOR, ORIENTATION } from '@shared/js/device';
import { isAppleDevice, isMobileAppleDevice, isTabletAppleDevice } from '@shared/utils/detectAppleDevice';

const MUTATION = {
	SET_DEVICE:             'SET_DEVICE',
	SET_FULLSCREEN:         'SET_FULLSCREEN',
	SET_ORIENTATION:        'SET_ORIENTATION',
	SET_SCREEN_PIXEL_RATIO: 'SET_SCREEN_PIXEL_RATIO',
	SET_TOUCH:              'SET_TOUCH',
	SET_VIEWPORT:           'SET_VIEWPORT',
	SET_WINDOW_HEIGHT:      'SET_WINDOW_HEIGHT',
	SET_WINDOW_WIDTH:       'SET_WINDOW_WIDTH',
};

const getInitialState = () => ({
	device:           null,
	fullscreen:       false,
	orientation:      ORIENTATION.PORTRAIT,
	screenPixelRatio: 0,
	touch:            false,
	viewport:         null,
	windowHeight:     0,
	windowWidth:      0,
});

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		[MUTATION.SET_DEVICE]:             (state, device) => (state.device = device),
		[MUTATION.SET_FULLSCREEN]:         (state, value) => (state.fullscreen = value),
		[MUTATION.SET_ORIENTATION]:        (state, value) => (state.orientation = value),
		[MUTATION.SET_SCREEN_PIXEL_RATIO]: (state, value) =>
			(state.screenPixelRatio = value),
		[MUTATION.SET_TOUCH]:         (state, value) => (state.touch = value),
		[MUTATION.SET_VIEWPORT]:      (state, value) => (state.viewport = value),
		[MUTATION.SET_WINDOW_HEIGHT]: (state, value) => (state.windowHeight = value),
		[MUTATION.SET_WINDOW_WIDTH]:  (state, value) => (state.windowWidth = value),
	},
	actions: {
		init({ commit }) {
			const device = new Device({
				store: {
					commit,
					mutations: {
						[SENSOR.FULLSCREEN]:         MUTATION.SET_FULLSCREEN,
						[SENSOR.ORIENTATION]:        MUTATION.SET_ORIENTATION,
						[SENSOR.SCREEN_PIXEL_RATIO]: MUTATION.SET_SCREEN_PIXEL_RATIO,
						[SENSOR.TOUCH]:              MUTATION.SET_TOUCH,
						[SENSOR.VIEWPORT]:           MUTATION.SET_VIEWPORT,
						[SENSOR.WINDOW_HEIGHT]:      MUTATION.SET_WINDOW_HEIGHT,
						[SENSOR.WINDOW_WIDTH]:       MUTATION.SET_WINDOW_WIDTH,
					},
				},
			});

			commit(MUTATION.SET_DEVICE, device);
		},
		async lockOrientation({ getters }) {
			if (getters.isMobile && getters.isFullscreen) {
				try {
					await screen.orientation.lock('landscape');
				} catch (error) {
					console.log('Unable to lock orientation:', { error });
				}
			}
		},
		async shareNative({ getters }, data) {
			if (getters.supportsNativeShare) {
				try {
					await navigator.share(data);
				} catch (error) {
					if (error.name !== 'AbortError') {
						console.error('Error sharing:', error);
					}
				}
			}
		},
	},
	getters: {
		fullscreen:          state => state.fullscreen,
		isFullscreen:        state => state.fullscreen,
		orientation:         state => state.orientation,
		screenPixelRatio:    state => state.screenPixelRatio,
		touch:               state => state.touch,
		isTouchDevice:       state => state.touch,
		viewport:            state => state.viewport,
		isMobileViewport:    state => state.viewport === 'mobile',
		isDesktopViewport:   state => state.viewport === 'desktop',
		isTabletViewport:    state => state.viewport === 'tablet',
		isNarrowAnalysis:    state => state.windowWidth < 1480, // Use to render one side panel in analysis instead of two
		windowHeight:        state => state.windowHeight,
		windowWidth:         state => state.windowWidth,
		isMobile:            state => state.touch && state.viewport === 'mobile',
		isMobilePortrait:    state => state.touch && state.viewport === 'mobile' && state.orientation === ORIENTATION.PORTRAIT,
		isMobileLandscape:   state => state.touch && state.viewport === 'mobile' && state.orientation === ORIENTATION.LANDSCAPE,
		isLandscape:         state => state.orientation === ORIENTATION.LANDSCAPE,
		isPortrait:          state => state.orientation === ORIENTATION.PORTRAIT,
		isTablet:            state => state.touch && state.viewport === 'tablet',
		isMobileOrTablet:    state => state.touch && (state.viewport === 'mobile' || state.viewport === 'tablet'),
		isAppleDevice:       _state => isAppleDevice(),
		isMobileAppleDevice: _state => isMobileAppleDevice(),
		isTabletAppleDevice: _state => isTabletAppleDevice(),
		supportsNativeShare: _state => navigator.share !== undefined,
	},
};
